/**
 * Custom SVG Line Chart — zero external dependencies.
 * Renders a polyline with gradient fill, Y-axis labels, and X-axis time scale.
 * Supports a compact "sparkline" mode for embedding in zone cards.
 *
 * When `timeRangeMs` is provided, data points are positioned by their actual
 * timestamp within the range [now - timeRangeMs, now]. This ensures switching
 * time ranges (1m → 10m → 1h) correctly scales the chart.
 */

import { useId } from 'react';

export interface DataPoint {
  t: number; // timestamp (ms)
  v: number; // value
}

export interface LineChartProps {
  data: DataPoint[];
  height?: number;
  color?: string;
  fillColor?: string;
  label?: string;
  className?: string;
  sparkline?: boolean;
  /** Total time range in ms. When set, X-axis uses timestamps instead of even spacing. */
  timeRangeMs?: number;
}

const PADDING = { top: 8, right: 8, bottom: 18, left: 48 };
const SPARKLINE_PADDING = { top: 2, right: 2, bottom: 2, left: 2 };
const GRID_LINES = 4;
const X_TICK_COUNT = 5; // Number of time axis labels

function formatAxisValue(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  if (n >= 10) return n.toFixed(0);
  if (n > 0) return n.toFixed(1);
  return '0';
}

/** Format seconds-ago into a readable time label */
function formatTimeLabel(secondsAgo: number): string {
  if (secondsAgo <= 0) return 'now';
  if (secondsAgo < 60) return `-${Math.round(secondsAgo)}s`;
  if (secondsAgo < 3600) return `-${Math.round(secondsAgo / 60)}m`;
  return `-${(secondsAgo / 3600).toFixed(1)}h`;
}

export function LineChart({
  data,
  height = 100,
  color = '#60a5fa',
  fillColor,
  label,
  className = '',
  sparkline = false,
  timeRangeMs,
}: LineChartProps) {
  const gradientId = useId();
  const pad = sparkline ? SPARKLINE_PADDING : PADDING;
  const width = 300; // viewBox width, scales responsively

  const chartW = width - pad.left - pad.right;
  const chartH = height - pad.top - pad.bottom;

  // Empty state
  if (data.length === 0) {
    return (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className={`w-full ${className}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={label ?? 'Line chart'}
      >
        <text
          x={width / 2}
          y={height / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#6b7280"
          fontSize={sparkline ? 10 : 12}
          data-testid="empty-text"
        >
          No data yet
        </text>
      </svg>
    );
  }

  // Determine time range for X-axis positioning
  const now = data.length > 0 ? Math.max(...data.map((d) => d.t)) : Date.now();
  const rangeMs = timeRangeMs ?? (data.length > 1 ? now - Math.min(...data.map((d) => d.t)) : 60_000);
  const tStart = now - rangeMs;

  const values = data.map((d) => d.v);
  const maxVal = Math.max(...values);
  const yMax = maxVal > 0 ? maxVal * 1.1 : 1; // 10% headroom, avoid divide-by-zero

  // Build polyline points — position by timestamp within range
  const points = data.map((d) => {
    const tFraction = rangeMs > 0 ? Math.max(0, Math.min(1, (d.t - tStart) / rangeMs)) : 0.5;
    const x = pad.left + tFraction * chartW;
    const y = pad.top + chartH - (d.v / yMax) * chartH;
    return { x, y, str: `${x},${y}` };
  });
  const polylineStr = points.map((p) => p.str).join(' ');

  // Build fill polygon (same points + bottom-right + bottom-left)
  const firstX = points[0].x;
  const lastX = points[points.length - 1].x;
  const bottomY = pad.top + chartH;
  const polygonStr = `${polylineStr} ${lastX},${bottomY} ${firstX},${bottomY}`;

  // Grid values for Y-axis
  const gridValues: number[] = [];
  if (!sparkline) {
    for (let i = 0; i <= GRID_LINES; i++) {
      gridValues.push((yMax / GRID_LINES) * i);
    }
  }

  // X-axis time ticks
  const timeTicks: { x: number; label: string }[] = [];
  if (!sparkline) {
    const rangeSec = rangeMs / 1000;
    for (let i = 0; i < X_TICK_COUNT; i++) {
      const fraction = i / (X_TICK_COUNT - 1);
      const x = pad.left + fraction * chartW;
      const secondsAgo = rangeSec * (1 - fraction);
      timeTicks.push({ x, label: formatTimeLabel(secondsAgo) });
    }
  }

  const resolvedFill = fillColor ?? color;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className={`w-full ${className}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={label ?? 'Line chart'}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={resolvedFill} stopOpacity="0.3" />
          <stop offset="100%" stopColor={resolvedFill} stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {/* Grid lines + Y labels (normal mode only) */}
      {!sparkline &&
        gridValues.map((val, i) => {
          const y = pad.top + chartH - (val / yMax) * chartH;
          return (
            <g key={i}>
              <line
                x1={pad.left}
                y1={y}
                x2={width - pad.right}
                y2={y}
                stroke="#374151"
                strokeWidth="0.5"
                data-testid="grid-line"
              />
              <text
                x={pad.left - 4}
                y={y}
                textAnchor="end"
                dominantBaseline="central"
                fill="#6b7280"
                fontSize="9"
              >
                {formatAxisValue(val)}
              </text>
            </g>
          );
        })}

      {/* X-axis time labels (normal mode only) */}
      {!sparkline &&
        timeTicks.map((tick, i) => (
          <text
            key={i}
            x={tick.x}
            y={height - 2}
            textAnchor="middle"
            fill="#6b7280"
            fontSize="8"
            data-testid="time-label"
          >
            {tick.label}
          </text>
        ))}

      {/* Gradient fill */}
      <polygon
        points={polygonStr}
        fill={`url(#${gradientId})`}
        data-testid="gradient-fill"
      />

      {/* Line */}
      <polyline
        points={polylineStr}
        fill="none"
        stroke={color}
        strokeWidth={sparkline ? 1.5 : 2}
        strokeLinejoin="round"
        strokeLinecap="round"
        data-testid="chart-line"
      />

      {/* Label (normal mode only) */}
      {!sparkline && label && (
        <text
          x={pad.left + 4}
          y={pad.top + 12}
          fill={color}
          fontSize="11"
          fontWeight="600"
          data-testid="chart-label"
        >
          {label}
        </text>
      )}
    </svg>
  );
}
